// ReconciliationCard — did the rail's balance move the way our payouts
// say it should?
//
// Admin-only. The rail never itemises a fee, and it debits at dispatch,
// refunds bank-side failures in full, and keeps the money of payouts we
// failed ourselves. So the only reconciliation possible is arithmetic on
// its balance:
//
//   expected closing = opening − dispatched + refunded
//   residual         = actual closing − expected closing
//
// Positive residual = money that arrived (a top-up). Negative = money that
// left with no payout of ours behind it — the rail's charges, or a
// question for them. Both are shown; nothing is netted away.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  getReconciliation,
  readBalanceNow,
  runDailyReconciliation,
  type ReconReport,
} from "@/api/reconciliation";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { I } from "@/components/Icons";
import { Pill } from "@/components/UI";
import { useUI } from "@/store/ui";
import { apiErrorMessage } from "@/utils/apiError";
import { dateRangeBounds, periodPresets } from "@/utils/dateRange";
import { fmtCcyMinor, formatDateTimeFull } from "@/utils/format";

export function ReconciliationCard({
  from,
  to,
  onRange,
}: {
  from: string;
  to: string;
  onRange: (r: { from: string; to: string }) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const addToast = useUI((s) => s.addToast);
  const presets = useMemo(() => periodPresets(), []);
  const range = useMemo(() => dateRangeBounds(from, to), [from, to]);
  const qk = ["reconciliation", range.from ?? null, range.to ?? null];
  const { data, isLoading, error } = useQuery({
    queryKey: qk,
    queryFn: () => getReconciliation(range),
    refetchInterval: 5 * 60_000,
  });

  const readMut = useMutation({
    mutationFn: readBalanceNow,
    onSuccess: () => {
      addToast({
        title: "Balance read",
        body: "A fresh reading was taken; figures refresh in a moment.",
      });
      void qc.invalidateQueries({ queryKey: ["reconciliation"] });
    },
    onError: (err) =>
      addToast({
        title: "Could not read the balance",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });
  const runMut = useMutation({
    mutationFn: () => runDailyReconciliation(from),
    onSuccess: () =>
      addToast({
        title: "Daily report sent",
        body: `The reconciliation for ${from} went to every alert destination.`,
      }),
    onError: (err) =>
      addToast({
        title: "Could not send",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });

  return (
    <div className="card" style={{ marginTop: "var(--card-gap)" }}>
      <div className="card-head">
        <h3>Balance reconciliation</h3>
        <span className="dim" style={{ fontSize: 12 }}>
          What the rail's balance did versus what our payouts say it should have
          done.
        </span>
        <div className="spacer" />
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => readMut.mutate()}
          disabled={readMut.isPending}
          title="Take a balance reading now"
        >
          <I.Refresh size={11} /> Read balance
        </button>
        <button
          className="btn sm"
          type="button"
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending || from === "" || from !== to}
          title={
            from !== to
              ? "Pick a single day to send its report"
              : `Send the report for ${from} to every alert destination`
          }
        >
          <I.Bell size={11} /> Send day report
        </button>
      </div>
      <div className="card-body">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <DateRangeFilter
            from={from}
            to={to}
            onChange={onRange}
            presets={presets}
            label="Period"
          />
        </div>
        {isLoading && <div className="dim">Loading…</div>}
        {error != null && (
          <div className="dim" style={{ color: "var(--warn)" }}>
            {apiErrorMessage(error, "Could not load the reconciliation.")}
          </div>
        )}
        {data && data.reports.length === 0 && (
          <div className="dim">
            No rail account with a live rail to reconcile.
          </div>
        )}
        {data?.reports.map((r) => (
          <ReportBlock
            key={r.account}
            r={r}
            threshold={data.maxChargePerPayoutMinor}
            onOpenPayout={(id) => navigate(`/payouts/${id}`)}
          />
        ))}
      </div>
    </div>
  );
}

function ReportBlock({
  r,
  threshold,
  onOpenPayout,
}: {
  r: ReconReport;
  threshold: bigint;
  onOpenPayout: (id: number) => void;
}) {
  const inr = (m: bigint) => fmtCcyMinor(m, "INR");
  const chargesHigh =
    r.dispatchedCount > 0 && r.chargePerPayoutMinor > threshold;
  const unexplained = r.dispatchedCount === 0 && r.chargesMinor > 100n;
  const tone =
    r.opening == null || r.closing == null
      ? ""
      : chargesHigh || unexplained || r.held.length > 0
        ? "warn"
        : "pos";
  const label =
    tone === "pos"
      ? "reconciles"
      : tone === "warn"
        ? "needs a look"
        : "incomplete";

  return (
    <section style={{ marginBottom: 16 }}>
      <div
        className="row"
        style={{ alignItems: "baseline", gap: 10, marginBottom: 8 }}
      >
        <h4 style={{ margin: 0, fontSize: 13 }}>
          Account <span className="mono">{r.account}</span>
        </h4>
        <Pill tone={tone}>{label}</Pill>
        <span className="dim" style={{ fontSize: 11 }}>
          shared by {r.merchants.length} merchant
          {r.merchants.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="kpi-grid">
        <Stat
          k="Opening"
          v={r.opening ? inr(r.opening.balanceMinor) : "—"}
          sub={
            r.opening
              ? `read ${formatDateTimeFull(r.opening.at)}`
              : "no reading before the period"
          }
        />
        <Stat
          k="Dispatched"
          v={`− ${inr(r.dispatchedMinor)}`}
          sub={`${r.dispatchedCount} payouts accepted by the rail`}
        />
        <Stat
          k="Refunded by the bank"
          v={`+ ${inr(r.refundedMinor)}`}
          sub={`${r.refundedCount} payouts the bank failed`}
        />
        <Stat
          k="Closing"
          v={r.closing ? inr(r.closing.balanceMinor) : "—"}
          sub={
            r.closing
              ? `read ${formatDateTimeFull(r.closing.at)}`
              : "no reading in the period"
          }
        />
      </div>
      <div className="kpi-grid" style={{ marginTop: 8 }}>
        <Stat
          k="Expected closing"
          v={r.opening && r.closing ? inr(r.expectedClosingMinor) : "—"}
          sub="opening − dispatched + refunded"
        />
        <Stat
          k="Credits (top-ups)"
          v={inr(r.creditsMinor)}
          sub="arrived without a payout of ours"
        />
        <Stat
          k="Charges"
          v={inr(r.chargesMinor)}
          sub={
            r.dispatchedCount > 0 && r.chargesMinor > 0n
              ? `${inr(r.chargePerPayoutMinor)} per payout`
              : "left without a payout of ours"
          }
          warn={chargesHigh || unexplained}
        />
        <Stat
          k="Held by the rail"
          v={inr(r.heldMinor)}
          sub={`${r.held.length} payouts we failed after acceptance`}
          warn={r.held.length > 0}
        />
      </div>

      {(r.warnings.length > 0 || chargesHigh || unexplained) && (
        <ul
          className="dim"
          style={{ fontSize: 12, margin: "10px 0 0", paddingLeft: 18 }}
        >
          {chargesHigh && (
            <li>
              Charges per payout are above the {inr(threshold)} threshold —
              check the rate card.
            </li>
          )}
          {unexplained && (
            <li>
              Money left the account with no payout behind it — ask the rail
              what it was.
            </li>
          )}
          {r.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {r.held.length > 0 && (
        <table className="table" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Payout</th>
              <th>Amount</th>
              <th>Dispatched</th>
              <th>Failed on our side</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {r.held.map((h) => (
              <tr key={h.payoutId}>
                <td>
                  <button
                    className="btn ghost sm"
                    type="button"
                    onClick={() => onOpenPayout(h.payoutId)}
                  >
                    #{h.payoutId}
                  </button>
                </td>
                <td className="mono">{inr(h.amountMinor)}</td>
                <td className="dim">{formatDateTimeFull(h.dispatchedAt)}</td>
                <td className="dim">{formatDateTimeFull(h.failedAt)}</td>
                <td className="dim">
                  {h.errorCode === "admin_force_cancelled"
                    ? "force-cancelled by an admin"
                    : "given up on while pending"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {r.held.length > 0 && (
        <p className="dim" style={{ fontSize: 11, marginTop: 6 }}>
          The rail accepted and debited these, then we marked them failed. The
          rail does not refund those on its own — ask for the money back,
          quoting the payout ids.
        </p>
      )}
    </section>
  );
}

function Stat({
  k,
  v,
  sub,
  warn,
}: {
  k: string;
  v: string;
  sub: string;
  warn?: boolean;
}) {
  return (
    <div className="kpi">
      <div className="k">{k}</div>
      <div className="v" style={warn ? { color: "var(--warn)" } : undefined}>
        {v}
      </div>
      <div
        className="row gap-12"
        style={{ alignItems: "center", marginTop: 6 }}
      >
        <span className="dim" style={{ fontSize: 11 }}>
          {sub}
        </span>
      </div>
    </div>
  );
}
